/* Renders the lab requisition home page */

import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Checkbox, FormControlLabel, useTheme } from '@material-ui/core';
import { DetailsLayout } from '../utilities/layouts/DetailsLayout';
import { ContentLayout } from '../utilities/layouts/ContentLayout';
import { PATH_HOME } from '../../containers/RoutePaths';
import { RequiresLogin } from '../utilities/commonUtilities/RequiresLogin';
import { LabRequisitionHomeStyle } from '../../styles/LabRequisitionHomeStyle';
import clsx from 'clsx';
import * as TaskNames from '../home/TaskNames';
import * as currentVisitAPI from '../../apis/currentVisit';
import AlertModalYesNo from '../utilities/AlertModal/AlertModalYesNo';
import { useCurrentVisit, useCurrentVisitAPI } from '../../userDataProviders/CurrentVisitProvider';

const ImagingHome = () => {
    const classes = LabRequisitionHomeStyle();
    const theme = useTheme();
    const currentVisit = useCurrentVisit();
    const { fetchCurrentVisit } = useCurrentVisitAPI();

    useEffect(() => {
        if (!currentVisit.visit) {
            fetchCurrentVisit();
        }
    }, [fetchCurrentVisit, currentVisit]);

    return (
        <>
            <RequiresLogin>
                <DetailsLayout title="Get an Imaging Test" backTo={PATH_HOME}>
                    <ContentLayout>
                        <Box className={classes.contentContainer}>
                            <InstructionList currentVisit={currentVisit} fetchCurrentVisit={fetchCurrentVisit} theme={theme} />
                            <br />
                            <Footer />
                        </Box>
                    </ContentLayout>
                </DetailsLayout>
            </RequiresLogin>
        </>
    );
};

export const InstructionList = ({ currentVisit, fetchCurrentVisit, theme }) => {
    const classes = LabRequisitionHomeStyle();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const imagingInstruction1 = 'You will receive a call confirmation from your imaging centre for appointment details regarding your imaging test.';
    const imagingInstruction2List = [
        'Your imaging requisition has been sent to your imaging center by the Watchman Coordinator',
        'Bring your health card',
        'Do not eat solid foods four hours before your imaging test',
    ];
    const imagingInstruction2 = (
        <ul className={classes.unorderedList}>
            {imagingInstruction2List.map((a, idx) => (
                <li key={idx.toString()}>{a}</li>
            ))}
        </ul>
    );
    const imagingInstruction3 = 'Your imaging test results will be sent to your doctor for review.';

    const instructions = [
        { text: 'Before the test', class: classes.firstGridRow, withIcon: true, instruction: imagingInstruction1 },
        { text: 'Preparing for the test', class: classes.firstGridRow, withIcon: true, instruction: imagingInstruction2 },
        { text: 'After the test', class: classes.firstGridRow, withIcon: true, instruction: imagingInstruction3 },
    ];

    // Find the imaging task in the current visit
    const imagingTask = currentVisit?.visit?.tasks?.find(
        task => task.nedvcTaskIdentifier === TaskNames.IMAGING
    );

    // Determine checkbox state based on task status
    const shouldShowCheckbox = !!imagingTask;
    const isTaskCompleted = imagingTask?.status === 'COMPLETED';
    const isTaskAccepted = imagingTask?.status === 'ACCEPTED';
    const isCheckboxDisabled = (!isTaskAccepted && !isTaskCompleted) || isSubmitting;
    const isCheckboxChecked = isTaskCompleted;

    // Reset `isSubmitting` once the task is marked as completed (after the API call and refresh)
    useEffect(() => {
        if (isSubmitting && isTaskCompleted) {
            setIsSubmitting(false);
        }
    }, [isSubmitting, isTaskCompleted]);

    const handleCheckboxClick = () => {
        if (!isCheckboxDisabled && !isCheckboxChecked) {
            setShowConfirmDialog(true);
        }
    };

    const handleConfirm = async () => {
        setIsSubmitting(true);
        setShowConfirmDialog(false);

        try {
            await currentVisitAPI.markImagingTaskComplete();
            // Refresh the current visit to get updated task status.
            // NOTE: Once we get a response from this request and the task status is `COMPLETED`, we will update `isSubmitting`
            // otherwise the user could accidentally submit again.
            if (fetchCurrentVisit) {
                fetchCurrentVisit();
            }
        } catch (error) {
            console.error('Error marking imaging task as complete:', error);
            // Reset on error so user can try marking the imaging task as complete again.
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    return (
        <>
            {instructions &&
                instructions.map((a, index) => (
                    <React.Fragment key={index.toString()}>
                        <InstructionRow IconText={index + 1} styling={classes.gridRow}>
                            <Grid item xs zeroMinWidth>
                                <Typography className={clsx(classes.listText, classes.bulletPointText)}>{a.text}</Typography>
                            </Grid>
                        </InstructionRow>
                        <InstructionRow styling={classes.gridRowLessTop}>
                            <Grid item xs zeroMinWidth>
                                <Typography component="div" className={classes.leftPaddedContent}>
                                    {a.instruction}
                                </Typography>
                            </Grid>
                        </InstructionRow>
                    </React.Fragment>
                ))}
            {shouldShowCheckbox && (
                <Box
                    display="flex"
                    alignItems="center"
                    mt={3}
                    mb={1}
                    style={{
                        backgroundColor: theme.palette.primary.light,
                        padding: theme.spacing(2, 3),
                        borderRadius: '4px',
                    }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isCheckboxChecked}
                                onChange={handleCheckboxClick}
                                disabled={isCheckboxDisabled}
                                style={{
                                    color: isCheckboxChecked ? theme.palette.warning.main : undefined,
                                }}
                            />
                        }
                        label={
                            <Typography style={{ color: 'black', fontWeight: 600 }}>
                                {isTaskCompleted ? "Task complete!" : "Mark task as complete"}
                            </Typography>
                        }
                    />
                </Box>
            )}
            <AlertModalYesNo
                isDisplayed={showConfirmDialog}
                alertModalTitle="Confirm Completion"
                alertModalBody="Confirm that you have completed your imaging test for this visit"
                onYesSelected={handleConfirm}
                onNoSelected={handleCancel}
                onCloseSelected={handleCancel}
                yesButtonText="Confirm"
                noButtonText="Cancel"
            />
        </>
    );
};

const InstructionRow = ({ Icon, IconText, styling, IconStyles, children }) => {
    const classes = LabRequisitionHomeStyle();
    return (
        <Grid container className={styling ? styling : ''}>
            <Grid
                item
                className={clsx(classes.gridIcon, {
                    [IconStyles]: !!IconStyles,
                })}
            >
                {Icon && <Icon aria-labelledby="icon" />}
                {IconText && <BulletIcon IconText={IconText} />}
            </Grid>
            {children}
        </Grid>
    );
};

const BulletIcon = ({ IconText }) => {
    const classes = LabRequisitionHomeStyle();
    return (
        <div className={classes.bulletIcon}>
            <Typography component="span">{IconText}</Typography>
        </div>
    );
};

const Footer = () => {
    const classes = LabRequisitionHomeStyle();
    return (
        <Box className={classes.footer} display="flex" flexDirection="column">
            <Typography variant="caption" className={classes.footTypographyTitle}>
                Keep in Mind
            </Typography>
            <Typography variant="caption">
                For patients getting an imaging test at Princess Margaret, Toronto General, or Toronto Western:
                <br />
                Please contact the Watchman coordinator if you have not received appointment details regarding your imaging test.
                <br />
                <br />
                For patients getting an imaging test at any non-UHN sites:
                <br />
                Please contact the imaging centre if you have not received appointment details regarding your imaging test.
            </Typography>
        </Box>
    );
};

export default ImagingHome;
